
import { supabase } from "@/integrations/supabase/client";
import { Team } from "@/types";

interface TeamLookupParams {
  urlSlug?: string | null;
  teamName?: string | null;
  clubAcronym?: string | null;
}

interface TeamLookupResult {
  team: Team | null;
  error: string | null;
}

/**
 * Finds a team based on URL parameters, with different lookup strategies
 * based on the available parameters
 */
export const lookupTeam = async ({ 
  urlSlug, 
  teamName, 
  clubAcronym 
}: TeamLookupParams): Promise<TeamLookupResult> => {
  try {
    console.log("TeamLookup - Starting team lookup with params:", {
      urlSlug,
      teamName,
      clubAcronym
    });
    
    // Variables to track search results
    let teamData = null;
    let teamError = null;
    
    // Try first with exact url_slug match (our preferred approach)
    if (urlSlug) {
      console.log("TeamLookup - Using short URL format with slug:", urlSlug);

      // SECURITY DEFINER RPC returns at most one team matching the exact slug —
      // avoids granting anon direct SELECT on teamvote_teams (which would let
      // anyone enumerate every team with a share slug).
      const { data, error } = await supabase.rpc('lookup_teamvote_team_by_slug', {
        p_slug: urlSlug,
      });

      teamData = Array.isArray(data) ? data[0] || null : null;
      teamError = error;

      console.log("TeamLookup - Team lookup by slug result:", {
        teamFound: !!teamData,
        error: teamError?.message
      });
    }
    // Legacy formats with team name
    else if (teamName) {
      console.log("TeamLookup - Using legacy URL format with team name:", teamName);

      const { data, error } = await supabase.rpc('lookup_teamvote_team_by_name', {
        p_name: teamName,
        p_club: clubAcronym ?? null,
      });

      teamData = Array.isArray(data) ? data[0] || null : null;
      teamError = error;
    }

    else {
      console.error("TeamLookup - No valid identifiers found in params");
      return { 
        team: null, 
        error: "Invalid team URL. Please check the URL and try again." 
      };
    }
    
    console.log("TeamLookup - Team lookup final result:", { 
      teamFound: !!teamData, 
      teamId: teamData?.id,
      teamName: teamData?.name,
      error: teamError?.message 
    });
    
    if (!teamData) {
      const identifier = urlSlug || teamName || "this team";
      return { 
        team: null, 
        error: `We couldn't find a team matching "${identifier}". Please check the URL and try again.` 
      };
    }
    
    // Store the team info in localStorage for PWA url generation
    if (teamData.id) {
      localStorage.setItem(`team_${teamData.id}`, JSON.stringify({
        name: teamData.name,
        club_acronym: teamData.club_acronym,
        url_slug: teamData.url_slug
      }));
    }
    
    return { team: teamData as Team, error: null };
  } catch (error) {
    console.error("Error in team lookup:", error);
    return { 
      team: null, 
      error: "Something went wrong looking up the team. Please try again later."
    };
  }
};
